/**
 * PopupReportTable - A report table for popup windows with row actions
 *
 * This table component reuses the same column creation logic as the main
 * table but sends events to the main window via PopupEvents for actions.
 */
import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  LoadingSpinner,
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ais-ui/ui-core';
import { ReportShipData } from '@ais-ui/websockets';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { PiDotsThreeVerticalBold } from 'react-icons/pi';
import { cn } from '@ais-ui/utils';
import { usePopupTableActions, RowAction } from './usePopupTableActions';
import { usePopupTableColumns } from './usePopupTableColumns';
import { useLocalization } from '@ais-ui/locale-management';

interface PopupReportTableProps {
  shipData: ReportShipData[] | null;
  isLoading: boolean;
  jobId: string | undefined;
  reportType: string;
}

/**
 * Row actions menu component
 */
const RowActionsMenu: React.FC<{
  row: Record<string, unknown>;
  actions: RowAction[];
  buttonClassName?: string;
  iconClassName?: string;
}> = ({
  row,
  actions,
  buttonClassName = 'size-8 p-0',
  iconClassName = 'size-5',
}) => {
  if (actions.length === 0) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className={buttonClassName}>
          <PiDotsThreeVerticalBold className={iconClassName} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action, index) => (
          <DropdownMenuItem key={index} onClick={() => action.onClick(row)}>
            {action.icon && <span className="mr-2">{action.icon}</span>}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const PopupReportTable: React.FC<PopupReportTableProps> = ({
  shipData,
  isLoading,
  jobId,
  reportType,
}) => {
  const { messages } = useLocalization();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const noDataMessage =
    messages.emptyStateNoData?.replace('{entity}', 'report') ??
    'No report data available';

  // Use the popup-specific hooks - reuses same column logic as main table
  const { getActionsForRow } = usePopupTableActions({
    shipData: shipData || undefined,
  });
  const { columns: baseColumns } = usePopupTableColumns({
    shipData: shipData || undefined,
  });

  // Fixed density for popups (compact by default)
  const densityClasses = {
    text: 'text-xs',
    tableCell: 'py-1 px-2',
    tableHeader: 'py-1 px-2',
    cardHeader: 'p-2',
    button: 'h-6 w-6',
    icon: { medium: 'h-4 w-4' },
  };

  // Add actions column to the base columns
  const columns = useMemo(() => {
    if (baseColumns.length === 0) return [];

    return [
      ...baseColumns,
      {
        id: 'actions',
        header: '',
        cell: ({ row }: { row: any }) => {
          const actions = getActionsForRow(row.original);
          return (
            <RowActionsMenu
              row={row.original as Record<string, unknown>}
              actions={actions}
              buttonClassName={densityClasses.button}
              iconClassName={densityClasses.icon.medium}
            />
          );
        },
        size: 50,
      },
    ];
  }, [baseColumns, getActionsForRow]);

  const table = useReactTable({
    data: shipData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (!shipData || shipData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        {noDataMessage}
      </div>
    );
  }

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className={cn('flex-shrink-0', densityClasses.cardHeader)}>
        <div className="flex justify-between items-center">
          <div className={cn('text-muted-foreground', densityClasses.text)}>
            {shipData.length} rows
          </div>
          <div className="flex items-center gap-4">
            <span className={cn('text-muted-foreground', densityClasses.text)}>
              Page {currentPage + 1} of {pageCount}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="default"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="default"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>

      <div className="flex-1 overflow-hidden px-4 pb-4">
        <ScrollArea className="h-full border rounded-md">
          <Table container={false}>
            <TableHeader className="sticky top-0 bg-background z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'truncate font-medium',
                        densityClasses.tableHeader,
                        densityClasses.text,
                      )}
                      style={{
                        width: header.getSize(),
                        minWidth: header.id === 'actions' ? 50 : 100,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="odd:bg-muted/50">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(densityClasses.tableCell)}
                      style={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.id === 'actions' ? 50 : 100,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    </Card>
  );
};
